import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  NotificationBar,
  Notification,
  TrackableLink,
  withContentCards,
} from '@surfline/quiver-react';
import { BrazeContentCards, dismissContentCard } from '@surfline/web-common';
import { CLICKED_SUBSCRIBE_CTA } from '../../common/constants';

const SUBSCRIBE_CTA_PROPERTIES = { ctaLocation: 'upsell bar' };

const NotificationBarController = ({ card, isPremium, warnings, slDisableNotificationCTA }) => {
  const freeTrialLinkRef = useRef(null);

  const renderBrazeNotification = useMemo(
    () =>
      card?.extras && (
        <Notification
          closable
          type="large"
          level="information"
          onCloseComplete={() => dismissContentCard(BrazeContentCards.NOTIFICATION_BAR)}
        >
          <TrackableLink
            ref={freeTrialLinkRef}
            eventName={CLICKED_SUBSCRIBE_CTA}
            eventProperties={SUBSCRIBE_CTA_PROPERTIES}
          >
            <a
              ref={freeTrialLinkRef}
              href={card.extras.href__url}
              className="quiver-notification__tag-line"
              style={{ ...card.extras.css }}
            >
              {card.extras.href__text}
            </a>
          </TrackableLink>
        </Notification>
      ),
    [card],
  );

  const renderPaymentWarningsNotification = useMemo(() => {
    const computeWarningType = () => {
      const warningTypes = warnings.map(({ type }) => type);
      if (warningTypes.includes('failedPayment')) return 'failedPayment';
      if (warningTypes.includes('invalidZip') && isPremium) return 'invalidZip';
      return null;
    };
    const warningType = computeWarningType();
    let notification =
      'We are unable to renew your membership due to a payment problem. To keep Surfline Premium, ';
    const linkText = 'Please update your payment details.';
    if (warningType === 'invalidZip') {
      notification = 'Make sure you keep Surfline Premium. ';
    }
    return (
      <div>
        {warningType ? (
          <Notification closable type="warning-notification" level="error">
            <span>{notification}</span>
            <a href="/account/subscription#payment-details">{linkText}</a>
          </Notification>
        ) : null}
      </div>
    );
  }, [isPremium, warnings]);

  const renderNotification = useMemo(() => {
    if (slDisableNotificationCTA === 'on') return null;
    if (warnings.length) return renderPaymentWarningsNotification;
    if (card && card.extras) return renderBrazeNotification;
    return null;
  }, [
    card,
    renderPaymentWarningsNotification,
    renderBrazeNotification,
    slDisableNotificationCTA,
    warnings,
  ]);

  const [notification, setNotification] = useState(renderNotification);
  // Updates the bar when the Braze card is set
  useEffect(() => {
    setNotification(renderNotification);
  }, [renderNotification]);

  return <NotificationBar largeNotification={notification} />;
};

NotificationBarController.propTypes = {
  card: PropTypes.shape({
    name: PropTypes.string,
    extras: PropTypes.shape({
      css: PropTypes.objectOf(PropTypes.shape),
      href__text: PropTypes.string,
      href__url: PropTypes.string,
    }),
  }),
  isPremium: PropTypes.bool.isRequired,
  slDisableNotificationCTA: PropTypes.string,
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      subscriptionId: PropTypes.string,
      cardId: PropTypes.string,
      planId: PropTypes.string,
      type: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
};

NotificationBarController.defaultProps = {
  card: { name: BrazeContentCards.NOTIFICATION_BAR, extras: null },
  slDisableNotificationCTA: 'off',
  warnings: [],
};

export default withContentCards(NotificationBarController);
